import {
  useId,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Calculator, ChevronDown, GitMerge, Layers, Plus, Variable } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DialogClose,
  FeedbackState,
  Field,
  FormDrawer,
  Input,
  SearchInput,
  Segmented,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
} from "@h2j-data-team/ui";
import type { GroupApp, QlikDimension, QlikMeasure, QlikVariable } from "@repo/shared";

import { toast } from "@/components/shared";
import { getErrorMessage } from "@/lib/api-client";
import { discardMessages } from "@/lib/ui-labels";
import { cn } from "@/lib/utils";
import {
  useCreateGroupVariable,
  useCreateGroupDimension,
  useCreateGroupMeasure,
} from "@/features/groups/api";
import { appMetadataQueryOptions } from "@/features/qlik-connections/api";

/**
 * The selection cell of the library's DataTable (fixed width, a checkbox that fits the row),
 * for the tables composed from the Table primitives here, which have no such variant.
 */
const SELECT_CELL = "h2j-data-table__select";

// =============================================================================
// Merge helpers
// =============================================================================

type MergedItem<T> = {
  key: string; // name/title, or a synthetic id for a cross-name merge group
  presentIn: Array<{ app: GroupApp; value: T }>; // one entry per app that has this item
  hasConflict: boolean; // true when values differ across apps
  selectedAppIndex: number; // which presentIn entry to use on import (default 0)
  canonicalName?: string; // standardized name for a cross-name merge (overrides the source name)
  mergedKeys?: string[]; // for a merge group: the original per-app names/titles being merged
  mergeType?: MergeItemType; // set on merge-group items so a row can act on the right type
};

function variableConflictKey(v: QlikVariable): string {
  return v.definition ?? "";
}
function dimensionConflictKey(d: QlikDimension): string {
  return d.fieldDefs.join(",");
}
function measureConflictKey(m: QlikMeasure): string {
  return m.expression ?? "";
}

function compareAlphabetically(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

// A user-confirmed merge of several differently-named items into one canonical item.
type MergeGroup = { id: string; memberKeys: string[]; canonicalName: string };
type MergeItemType = "variables" | "dimensions" | "measures";

// Deterministic id for a merge group so the same set can't be added twice.
function mergeGroupId(memberKeys: string[]): string {
  return "merge::" + [...memberKeys].sort().join("\u0000");
}

// Collapse merged members of a name-keyed base list into synthetic group items.
function applyMerges<T>(
  base: MergedItem<T>[],
  merges: MergeGroup[],
  conflictKey: (v: T) => string,
  type: MergeItemType,
): MergedItem<T>[] {
  if (merges.length === 0) return base;
  const byKey = new Map(base.map((it) => [it.key, it]));
  const consumed = new Set<string>();
  const groups: MergedItem<T>[] = [];
  for (const g of merges) {
    const members = g.memberKeys
      .map((k) => byKey.get(k))
      .filter((it): it is MergedItem<T> => Boolean(it));
    if (members.length < 2) continue;
    members.forEach((m) => consumed.add(m.key));
    const presentIn = members.flatMap((m) => m.presentIn);
    groups.push({
      key: g.id,
      presentIn,
      hasConflict: new Set(presentIn.map((p) => conflictKey(p.value))).size > 1,
      selectedAppIndex: 0,
      canonicalName: g.canonicalName,
      mergedKeys: members.map((m) => m.key),
      mergeType: type,
    });
  }
  const remaining = base.filter((it) => !consumed.has(it.key));
  return [...groups, ...remaining];
}

// Suggest merging differently-named items that share identical content.
function computeMergeSuggestions<T>(
  base: MergedItem<T>[],
  merges: MergeGroup[],
  conflictKey: (v: T) => string,
): { memberKeys: string[] }[] {
  const alreadyMerged = new Set(merges.flatMap((g) => g.memberKeys));
  const byContent = new Map<string, MergedItem<T>[]>();
  for (const it of base) {
    if (alreadyMerged.has(it.key)) continue;
    const value = it.presentIn[0]?.value;
    if (!value) continue;
    const content = conflictKey(value).trim();
    if (!content) continue;
    const arr = byContent.get(content) ?? [];
    arr.push(it);
    byContent.set(content, arr);
  }
  const suggestions: { memberKeys: string[] }[] = [];
  for (const items of byContent.values()) {
    if (items.length >= 2) {
      suggestions.push({ memberKeys: items.map((i) => i.key) });
    }
  }
  return suggestions;
}

interface UnifiedImportDrawerProps {
  /** The control that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  groupApps: GroupApp[];
  existingVariableNames: string[];
  existingDimensionTitles: string[];
  existingMeasureTitles: string[];
}

export function UnifiedImportDrawer({
  open,
  onOpenChange,
  groupId,
  groupApps,
  existingVariableNames,
  existingDimensionTitles,
  existingMeasureTitles,
  restoreFocusRef,
}: UnifiedImportDrawerProps) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  // --- State ---
  const [selectedGroupAppId, setSelectedGroupAppId] = useState<string>("");
  const [selectedVariables, setSelectedVariables] = useState<Set<string>>(new Set());
  const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(new Set());
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  // Step 3: mode toggle + expanded/source-selection state
  const [importMode, setImportMode] = useState<"single" | "all">("single");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedAppIndexes, setSelectedAppIndexes] = useState<Record<string, number>>({});
  const [activeTab, setActiveTab] = useState("variables");
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(["variables"]));
  // Cross-name merges (different names, same thing) confirmed by the user, per type.
  const [variableMerges, setVariableMerges] = useState<MergeGroup[]>([]);
  const [dimensionMerges, setDimensionMerges] = useState<MergeGroup[]>([]);
  const [measureMerges, setMeasureMerges] = useState<MergeGroup[]>([]);

  // --- Mutations ---
  const createVariableMutation = useCreateGroupVariable();
  const createDimensionMutation = useCreateGroupDimension();
  const createMeasureMutation = useCreateGroupMeasure();

  // --- Single-mode derived values ---
  const selectedGroupApp = groupApps.find((a) => a.id === selectedGroupAppId);
  const connectionId = selectedGroupApp?.connectionId ?? "";
  const appId = selectedGroupApp?.appId ?? "";

  // --- Single-mode queries ---
  const { data: singleMetadata, isLoading: isLoadingSingleMetadata } = useQuery({
    ...appMetadataQueryOptions(connectionId, appId),
    enabled: open && !!connectionId && !!appId && importMode === "single",
  });
  const variables = singleMetadata?.variables;
  const dimensions = singleMetadata?.dimensions;
  const measures = singleMetadata?.measures;
  // --- Step 6: All-mode fan-out queries ---
  const allMetadataQueries = useQueries({
    queries:
      importMode === "all" && open
        ? groupApps.map((app) => ({
            ...appMetadataQueryOptions(app.connectionId, app.appId),
            enabled: true,
          }))
        : [],
  });

  const isLoadingAllVariables =
    importMode === "all" && open && visitedTabs.has("variables")
      ? allMetadataQueries.some((q) => q.isLoading)
      : false;
  const isLoadingAllDimensions =
    importMode === "all" && open && visitedTabs.has("dimensions")
      ? allMetadataQueries.some((q) => q.isLoading)
      : false;
  const isLoadingAllMeasures =
    importMode === "all" && open && visitedTabs.has("measures")
      ? allMetadataQueries.some((q) => q.isLoading)
      : false;

  // --- Step 7: All-mode merge memos ---
  const mergedVariables = useMemo<MergedItem<QlikVariable>[]>(() => {
    if (importMode !== "all") return [];
    const map = new Map<string, MergedItem<QlikVariable>>();
    groupApps.forEach((app, idx) => {
      const items = (allMetadataQueries[idx]?.data?.variables ?? []) as QlikVariable[];
      const seenInApp = new Set<string>();
      for (const v of items) {
        if (v.isScriptCreated) continue;
        if (existingVariableNames.includes(v.name)) continue;
        if (seenInApp.has(v.name)) continue;
        seenInApp.add(v.name);
        const existing = map.get(v.name);
        if (existing) {
          existing.presentIn.push({ app, value: v });
        } else {
          map.set(v.name, {
            key: v.name,
            presentIn: [{ app, value: v }],
            hasConflict: false,
            selectedAppIndex: 0,
          });
        }
      }
    });
    for (const item of map.values()) {
      item.hasConflict = new Set(item.presentIn.map((p) => variableConflictKey(p.value))).size > 1;
    }
    return Array.from(map.values())
      .filter(
        (item) =>
          !searchQuery ||
          item.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.presentIn.some((p) =>
            p.value.definition?.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
      )
      .sort((a, b) => compareAlphabetically(a.key, b.key));
  }, [importMode, allMetadataQueries, groupApps, existingVariableNames, searchQuery]);

  const mergedDimensions = useMemo<MergedItem<QlikDimension>[]>(() => {
    if (importMode !== "all") return [];
    const map = new Map<string, MergedItem<QlikDimension>>();
    groupApps.forEach((app, idx) => {
      const items = (allMetadataQueries[idx]?.data?.dimensions ?? []) as QlikDimension[];
      const seenInApp = new Set<string>();
      for (const d of items) {
        if (existingDimensionTitles.includes(d.title)) continue;
        if (seenInApp.has(d.title)) continue;
        seenInApp.add(d.title);
        const existing = map.get(d.title);
        if (existing) {
          existing.presentIn.push({ app, value: d });
        } else {
          map.set(d.title, {
            key: d.title,
            presentIn: [{ app, value: d }],
            hasConflict: false,
            selectedAppIndex: 0,
          });
        }
      }
    });
    for (const item of map.values()) {
      item.hasConflict = new Set(item.presentIn.map((p) => dimensionConflictKey(p.value))).size > 1;
    }
    return Array.from(map.values())
      .filter(
        (item) =>
          !searchQuery ||
          item.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.presentIn.some((p) =>
            p.value.fieldDefs.some((f) => f.toLowerCase().includes(searchQuery.toLowerCase())),
          ),
      )
      .sort((a, b) => compareAlphabetically(a.key, b.key));
  }, [importMode, allMetadataQueries, groupApps, existingDimensionTitles, searchQuery]);

  const mergedMeasures = useMemo<MergedItem<QlikMeasure>[]>(() => {
    if (importMode !== "all") return [];
    const map = new Map<string, MergedItem<QlikMeasure>>();
    groupApps.forEach((app, idx) => {
      const items = (allMetadataQueries[idx]?.data?.measures ?? []) as QlikMeasure[];
      const seenInApp = new Set<string>();
      for (const m of items) {
        if (existingMeasureTitles.includes(m.title)) continue;
        if (seenInApp.has(m.title)) continue;
        seenInApp.add(m.title);
        const existing = map.get(m.title);
        if (existing) {
          existing.presentIn.push({ app, value: m });
        } else {
          map.set(m.title, {
            key: m.title,
            presentIn: [{ app, value: m }],
            hasConflict: false,
            selectedAppIndex: 0,
          });
        }
      }
    });
    for (const item of map.values()) {
      item.hasConflict = new Set(item.presentIn.map((p) => measureConflictKey(p.value))).size > 1;
    }
    return Array.from(map.values())
      .filter(
        (item) =>
          !searchQuery ||
          item.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.presentIn.some((p) =>
            p.value.expression?.toLowerCase().includes(searchQuery.toLowerCase()),
          ),
      )
      .sort((a, b) => compareAlphabetically(a.key, b.key));
  }, [importMode, allMetadataQueries, groupApps, existingMeasureTitles, searchQuery]);

  // --- Cross-name merge: apply user merges + compute content-based suggestions ---
  const displayedVariables = useMemo(
    () => applyMerges(mergedVariables, variableMerges, variableConflictKey, "variables"),
    [mergedVariables, variableMerges],
  );
  const displayedDimensions = useMemo(
    () => applyMerges(mergedDimensions, dimensionMerges, dimensionConflictKey, "dimensions"),
    [mergedDimensions, dimensionMerges],
  );
  const displayedMeasures = useMemo(
    () => applyMerges(mergedMeasures, measureMerges, measureConflictKey, "measures"),
    [mergedMeasures, measureMerges],
  );
  const variableSuggestions = useMemo(
    () => computeMergeSuggestions(mergedVariables, variableMerges, variableConflictKey),
    [mergedVariables, variableMerges],
  );
  const dimensionSuggestions = useMemo(
    () => computeMergeSuggestions(mergedDimensions, dimensionMerges, dimensionConflictKey),
    [mergedDimensions, dimensionMerges],
  );
  const measureSuggestions = useMemo(
    () => computeMergeSuggestions(mergedMeasures, measureMerges, measureConflictKey),
    [mergedMeasures, measureMerges],
  );

  const createMerge = (type: MergeItemType, memberKeys: string[], canonicalName: string) => {
    if (memberKeys.length < 2) return;
    const id = mergeGroupId(memberKeys);
    const addGroup = (prev: MergeGroup[]) =>
      prev.some((g) => g.id === id) ? prev : [...prev, { id, memberKeys, canonicalName }];
    const selectGroup = (prev: Set<string>) => {
      const next = new Set(prev);
      memberKeys.forEach((k) => next.delete(k));
      next.add(id);
      return next;
    };
    if (type === "variables") {
      setVariableMerges(addGroup);
      setSelectedVariables(selectGroup);
    } else if (type === "dimensions") {
      setDimensionMerges(addGroup);
      setSelectedDimensions(selectGroup);
    } else {
      setMeasureMerges(addGroup);
      setSelectedMeasures(selectGroup);
    }
  };

  const removeMerge = (type: MergeItemType, id: string, memberKeys: string[]) => {
    const drop = (prev: MergeGroup[]) => prev.filter((g) => g.id !== id);
    const restore = (prev: Set<string>) => {
      const next = new Set(prev);
      next.delete(id);
      memberKeys.forEach((k) => next.add(k));
      return next;
    };
    if (type === "variables") {
      setVariableMerges(drop);
      setSelectedVariables(restore);
    } else if (type === "dimensions") {
      setDimensionMerges(drop);
      setSelectedDimensions(restore);
    } else {
      setMeasureMerges(drop);
      setSelectedMeasures(restore);
    }
  };

  const renameMerge = (type: MergeItemType, id: string, canonicalName: string) => {
    const rename = (prev: MergeGroup[]) =>
      prev.map((g) => (g.id === id ? { ...g, canonicalName } : g));
    if (type === "variables") setVariableMerges(rename);
    else if (type === "dimensions") setDimensionMerges(rename);
    else setMeasureMerges(rename);
  };

  // --- Single-mode filtered lists (unchanged) ---
  const filteredVariables = useMemo(() => {
    if (!variables) return [];
    return variables
      .filter((v: QlikVariable) => {
        if (v.isScriptCreated) return false;
        if (existingVariableNames.includes(v.name)) return false;
        return (
          searchQuery === "" ||
          v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          v.definition?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
      .sort((a, b) => compareAlphabetically(a.name, b.name));
  }, [variables, existingVariableNames, searchQuery]);

  const filteredDimensions = useMemo(() => {
    if (!dimensions) return [];
    return dimensions
      .filter((d: QlikDimension) => {
        if (existingDimensionTitles.includes(d.title)) return false;
        return (
          searchQuery === "" ||
          d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.fieldDefs.some((f) => f.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      })
      .sort((a, b) => compareAlphabetically(a.title, b.title));
  }, [dimensions, existingDimensionTitles, searchQuery]);

  const filteredMeasures = useMemo(() => {
    if (!measures) return [];
    return measures
      .filter((m: QlikMeasure) => {
        if (existingMeasureTitles.includes(m.title)) return false;
        return (
          searchQuery === "" ||
          m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.expression?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
      .sort((a, b) => compareAlphabetically(a.title, b.title));
  }, [measures, existingMeasureTitles, searchQuery]);

  // --- Step 8: Expanded-row helper ---
  function toggleExpanded(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  // --- Step 9: Per-item source-selection helper ---
  function activeIndexFor(key: string): number {
    return selectedAppIndexes[key] ?? 0;
  }

  const totalSelected = selectedVariables.size + selectedDimensions.size + selectedMeasures.size;
  const n = groupApps.length;

  // --- Step 13: Mode-switch handler ---
  const handleModeSwitch = (mode: "single" | "all") => {
    setImportMode(mode);
    setSelectedVariables(new Set());
    setSelectedDimensions(new Set());
    setSelectedMeasures(new Set());
    setSearchQuery("");
    setExpandedKeys(new Set());
    setSelectedAppIndexes({});
    setVariableMerges([]);
    setDimensionMerges([]);
    setMeasureMerges([]);
    setActiveTab("variables");
    setVisitedTabs(new Set(["variables"]));
  };

  // --- Step 13: Close handler ---
  const handleClose = () => {
    setSelectedGroupAppId("");
    setSelectedVariables(new Set());
    setSelectedDimensions(new Set());
    setSelectedMeasures(new Set());
    setSearchQuery("");
    setImportMode("single");
    setExpandedKeys(new Set());
    setSelectedAppIndexes({});
    setVariableMerges([]);
    setDimensionMerges([]);
    setMeasureMerges([]);
    setActiveTab("variables");
    setVisitedTabs(new Set(["variables"]));
    onOpenChange(false);
  };

  // --- Step 12: Import handler branched on importMode ---
  const handleImport = async () => {
    if (totalSelected === 0) return;
    setIsImporting(true);
    try {
      let successCount = 0;

      if (importMode === "single") {
        for (const name of selectedVariables) {
          const v = variables?.find((item: QlikVariable) => item.name === name);
          if (!v) continue;
          await createVariableMutation.mutateAsync({
            groupId,
            data: {
              name: v.name,
              definition: v.definition || "",
              description: v.description || undefined,
              sources: v.id ? [{ groupAppId: selectedGroupAppId, qlikObjectId: v.id }] : undefined,
            },
          });
          successCount++;
        }
        for (const title of selectedDimensions) {
          const d = dimensions?.find((item: QlikDimension) => item.title === title);
          if (!d) continue;
          await createDimensionMutation.mutateAsync({
            groupId,
            data: {
              title: d.title,
              fieldDefs: d.fieldDefs,
              fieldLabels: d.fieldLabels,
              grouping: (d.grouping as "N" | "H" | "C") ?? "N",
              description: d.description || undefined,
              tags: d.tags,
              labelExpression: d.labelExpression || "",
              sources: d.id ? [{ groupAppId: selectedGroupAppId, qlikObjectId: d.id }] : undefined,
            },
          });
          successCount++;
        }
        for (const title of selectedMeasures) {
          const m = measures?.find((item: QlikMeasure) => item.title === title);
          if (!m) continue;
          await createMeasureMutation.mutateAsync({
            groupId,
            data: {
              title: m.title,
              expression: m.expression || "",
              label: m.label || undefined,
              numberFormat: m.numberFormat,
              description: m.description || undefined,
              tags: m.tags,
              sources: m.id ? [{ groupAppId: selectedGroupAppId, qlikObjectId: m.id }] : undefined,
            },
          });
          successCount++;
        }
      } else {
        for (const key of selectedVariables) {
          const merged = displayedVariables.find((item) => item.key === key);
          if (!merged) continue;
          const v = merged.presentIn[activeIndexFor(key)]?.value;
          if (!v) continue;
          await createVariableMutation.mutateAsync({
            groupId,
            data: {
              name: merged.canonicalName ?? v.name,
              definition: v.definition || "",
              description: v.description || undefined,
              sources: merged.presentIn.map((p) => ({
                groupAppId: p.app.id,
                qlikObjectId: p.value.id,
              })),
            },
          });
          successCount++;
        }
        for (const key of selectedDimensions) {
          const merged = displayedDimensions.find((item) => item.key === key);
          if (!merged) continue;
          const d = merged.presentIn[activeIndexFor(key)]?.value;
          if (!d) continue;
          await createDimensionMutation.mutateAsync({
            groupId,
            data: {
              title: merged.canonicalName ?? d.title,
              fieldDefs: d.fieldDefs,
              fieldLabels: d.fieldLabels,
              grouping: (d.grouping as "N" | "H" | "C") ?? "N",
              description: d.description || undefined,
              tags: d.tags,
              labelExpression: d.labelExpression || "",
              sources: merged.presentIn.map((p) => ({
                groupAppId: p.app.id,
                qlikObjectId: p.value.id,
              })),
            },
          });
          successCount++;
        }
        for (const key of selectedMeasures) {
          const merged = displayedMeasures.find((item) => item.key === key);
          if (!merged) continue;
          const m = merged.presentIn[activeIndexFor(key)]?.value;
          if (!m) continue;
          await createMeasureMutation.mutateAsync({
            groupId,
            data: {
              title: merged.canonicalName ?? m.title,
              expression: m.expression || "",
              label: m.label || undefined,
              numberFormat: m.numberFormat,
              description: m.description || undefined,
              tags: m.tags,
              sources: merged.presentIn.map((p) => ({
                groupAppId: p.app.id,
                qlikObjectId: p.value.id,
              })),
            },
          });
          successCount++;
        }
      }

      toast.success(t("import.successImported", { count: successCount }));
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err, t("import.failedImport")));
    } finally {
      setIsImporting(false);
    }
  };

  const isLoadingItems = isLoadingSingleMetadata;

  // Per-kind data for the three panels: the list of the single-app mode, the merged list of
  // the all-apps mode, and the selection both modes write to.
  const toMergedRows = <T,>(
    items: MergedItem<T>[],
    detailOf: (value: T) => string | undefined,
  ): MergedRow[] =>
    items.map((item) => ({
      key: item.key,
      canonicalName: item.canonicalName,
      mergedKeys: item.mergedKeys,
      mergeType: item.mergeType,
      hasConflict: item.hasConflict,
      sources: item.presentIn.map((p) => ({
        appId: p.app.id,
        appName: p.app.appName,
        detail: detailOf(p.value),
      })),
    }));

  const panels: Record<MergeItemType, ImportPanelData> = {
    variables: {
      selected: selectedVariables,
      setSelected: setSelectedVariables,
      singleRows: filteredVariables.map((v: QlikVariable) => ({
        id: v.id,
        key: v.name,
        detail: v.definition || undefined,
      })),
      singleEmpty:
        variables?.length === 0
          ? t("import.noVariablesInApp")
          : searchQuery
            ? t("import.noVariablesMatchSearch")
            : t("import.allVariablesInGroup"),
      mergedRows: toMergedRows(displayedVariables, (v) => v.definition || undefined),
      baseKeys: new Set(mergedVariables.map((m) => m.key)),
      suggestions: variableSuggestions,
      isLoadingAll: isLoadingAllVariables,
    },
    dimensions: {
      selected: selectedDimensions,
      setSelected: setSelectedDimensions,
      singleRows: filteredDimensions.map((d: QlikDimension) => ({
        id: d.id,
        key: d.title,
        detail: d.fieldDefs.join(", "),
      })),
      singleEmpty:
        dimensions?.length === 0
          ? t("import.noDimensionsInApp")
          : searchQuery
            ? t("import.noDimensionsMatchSearch")
            : t("import.allDimensionsInGroup"),
      mergedRows: toMergedRows(displayedDimensions, (d) => d.fieldDefs.join(", ")),
      baseKeys: new Set(mergedDimensions.map((m) => m.key)),
      suggestions: dimensionSuggestions,
      isLoadingAll: isLoadingAllDimensions,
    },
    measures: {
      selected: selectedMeasures,
      setSelected: setSelectedMeasures,
      singleRows: filteredMeasures.map((m: QlikMeasure) => ({
        id: m.id,
        key: m.title,
        detail: m.expression || undefined,
      })),
      singleEmpty:
        measures?.length === 0
          ? t("import.noMeasuresInApp")
          : searchQuery
            ? t("import.noMeasuresMatchSearch")
            : t("import.allMeasuresInGroup"),
      mergedRows: toMergedRows(displayedMeasures, (m) => m.expression || undefined),
      baseKeys: new Set(mergedMeasures.map((m) => m.key)),
      suggestions: measureSuggestions,
      isLoadingAll: isLoadingAllMeasures,
    },
  };

  const tabs: { kind: MergeItemType; label: string; icon: ReactNode }[] = [
    { kind: "variables", label: t("tabs.variables"), icon: <Variable /> },
    { kind: "dimensions", label: t("tabs.dimensions"), icon: <Layers /> },
    { kind: "measures", label: t("tabs.measures"), icon: <Calculator /> },
  ];

  const showItems = importMode === "single" ? !!selectedGroupAppId : groupApps.length > 0;

  return (
    <FormDrawer
      open={open}
      onOpenChange={handleClose}
      size="lg"
      closeLabel={tc("close")}
      title={t("import.dialogTitle")}
      description={
        importMode === "single"
          ? t("import.dialogDescriptionSingle")
          : t("import.dialogDescriptionAll")
      }
      restoreFocusRef={restoreFocusRef}
      busy={isImporting}
      dirty={totalSelected > 0}
      discardConfirmation={discardMessages()}
      footer={
        <>
          <DialogClose asChild>
            <Button variant="outline">{t("import.cancel")}</Button>
          </DialogClose>
          <Button onClick={handleImport} disabled={totalSelected === 0} loading={isImporting}>
            {isImporting ? (
              t("import.importing")
            ) : (
              <>
                <Plus />
                {totalSelected > 0
                  ? t("import.importCount", { count: totalSelected })
                  : t("import.import")}
              </>
            )}
          </Button>
        </>
      }
    >
      <div className="flex min-w-0 flex-col gap-4">
        {/* Mode toggle */}
        <Segmented<"single" | "all">
          label={t("import.modeLabel")}
          value={importMode}
          onValueChange={handleModeSwitch}
          options={[
            { value: "single", label: t("import.fromSingleApp") },
            { value: "all", label: t("import.fromAllApps") },
          ]}
        />

        {/* App selector — single mode only */}
        {importMode === "single" &&
          (groupApps.length === 0 ? (
            <p className="text-sm text-dim">{t("import.noAppsYet")}</p>
          ) : (
            <Field label={t("import.sourceAppLabel")}>
              {(control) => (
                <Select
                  value={selectedGroupAppId}
                  onValueChange={(value) => {
                    setSelectedGroupAppId(value);
                    setSelectedVariables(new Set());
                    setSelectedDimensions(new Set());
                    setSelectedMeasures(new Set());
                    setSearchQuery("");
                  }}
                >
                  <SelectTrigger {...control}>
                    <SelectValue placeholder={t("import.sourceAppPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {groupApps.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                        <span>{app.appName}</span>
                        <span className="ml-2 text-xs text-dim">
                          {[app.connectionName, app.spaceName, app.ownerName]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          ))}

        {/* All-mode: no apps warning */}
        {importMode === "all" && groupApps.length === 0 && (
          <p className="text-sm text-dim">{t("import.noAppsYet")}</p>
        )}

        {/* Items content — single: requires app selection; all: always shown if apps exist */}
        {showItems && (
          <>
            <SearchInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              clearLabel={tc("list.clearSearch")}
              placeholder={t("import.searchPlaceholder")}
              aria-label={t("import.searchPlaceholder")}
            />

            {importMode === "all" && <p className="text-xs text-dim">{t("import.mergeHint")}</p>}

            {importMode === "single" && isLoadingItems ? (
              <FeedbackState state="loading" title={tc("loading")} />
            ) : (
              <Tabs
                label={t("import.dialogTitle")}
                value={activeTab}
                onValueChange={(tab) => {
                  setActiveTab(tab);
                  setVisitedTabs((prev) => new Set([...prev, tab]));
                }}
                className="[&>[role=tabpanel]]:pt-3"
                items={tabs.map(({ kind, label, icon }) => ({
                  value: kind,
                  label: (
                    <span className="inline-flex items-center gap-1.5 [&>svg]:size-3.5">
                      {icon}
                      {label}
                      {panels[kind].selected.size > 0 && (
                        <Badge variant="secondary">{panels[kind].selected.size}</Badge>
                      )}
                    </span>
                  ),
                  content: (
                    <ImportItemsPanel
                      kind={kind}
                      mode={importMode}
                      data={panels[kind]}
                      searchActive={searchQuery !== ""}
                      appCount={n}
                      expandedKeys={expandedKeys}
                      onToggleExpanded={toggleExpanded}
                      activeIndexFor={activeIndexFor}
                      onPickSource={(key, index) =>
                        setSelectedAppIndexes((prev) => ({ ...prev, [key]: index }))
                      }
                      onMerge={(memberKeys) => createMerge(kind, memberKeys, memberKeys[0])}
                      onUnmerge={(id, memberKeys) => removeMerge(kind, id, memberKeys)}
                      onRename={(id, name) => renameMerge(kind, id, name)}
                    />
                  ),
                }))}
              />
            )}

            {totalSelected > 0 && (
              <p className="text-sm text-dim" aria-live="polite">
                {t("import.selected", { count: totalSelected })}
              </p>
            )}
          </>
        )}
      </div>
    </FormDrawer>
  );
}

// =============================================================================
// One tab of the import drawer
// =============================================================================

/** A row of the single-app list. */
interface SingleRow {
  id: string;
  key: string;
  detail?: string;
}

/** A row of the all-apps list: one item, possibly present with different values in several apps. */
interface MergedRow {
  key: string;
  canonicalName?: string;
  mergedKeys?: string[];
  mergeType?: MergeItemType;
  hasConflict: boolean;
  sources: { appId: string; appName: string; detail?: string }[];
}

interface ImportPanelData {
  selected: Set<string>;
  setSelected: Dispatch<SetStateAction<Set<string>>>;
  singleRows: SingleRow[];
  /** Why the single-app list is empty. */
  singleEmpty: string;
  mergedRows: MergedRow[];
  /** Keys of the items before any merge: only these can be merged. */
  baseKeys: Set<string>;
  suggestions: { memberKeys: string[] }[];
  isLoadingAll: boolean;
}

function ImportItemsPanel({
  kind,
  mode,
  data,
  searchActive,
  appCount,
  expandedKeys,
  onToggleExpanded,
  activeIndexFor,
  onPickSource,
  onMerge,
  onUnmerge,
  onRename,
}: {
  kind: MergeItemType;
  mode: "single" | "all";
  data: ImportPanelData;
  searchActive: boolean;
  appCount: number;
  expandedKeys: Set<string>;
  onToggleExpanded: (key: string) => void;
  activeIndexFor: (key: string) => number;
  onPickSource: (key: string, index: number) => void;
  onMerge: (memberKeys: string[]) => void;
  onUnmerge: (id: string, memberKeys: string[]) => void;
  onRename: (id: string, name: string) => void;
}) {
  const { t } = useTranslation("sync-groups");
  const { t: tc } = useTranslation("common");
  const uid = useId();
  const { selected, setSelected } = data;
  const all = mode === "all";
  const keys = all ? data.mergedRows.map((row) => row.key) : data.singleRows.map((row) => row.key);
  const selectedBase = all ? [...selected].filter((key) => data.baseKeys.has(key)) : [];

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const loading = all && data.isLoadingAll;
  const emptyText = all
    ? searchActive
      ? t("import.noItemsMatchSearch")
      : t("import.allItemsInGroup")
    : data.singleEmpty;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-dim">{t("import.available", { count: keys.length })}</span>
        <div className="flex items-center gap-1">
          {selectedBase.length >= 2 && (
            <Button
              type="button"
              variant="ghost"
              size="compact"
              className="text-brand-text"
              onClick={() => onMerge(selectedBase)}
            >
              <GitMerge />
              {t("import.mergeSelected", { count: selectedBase.length })}
            </Button>
          )}
          {keys.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="compact"
              onClick={() => setSelected(selected.size === keys.length ? new Set() : new Set(keys))}
            >
              {selected.size === keys.length ? t("import.deselectAll") : t("import.selectAll")}
            </Button>
          )}
        </div>
      </div>

      {all && data.suggestions.length > 0 && (
        <Alert tone="info" title={t("import.suggestedMatches")}>
          <ul className="mt-1 flex max-h-40 flex-col gap-1.5 overflow-y-auto">
            {data.suggestions.map((suggestion) => (
              <li
                key={suggestion.memberKeys.join("|")}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="min-w-0 flex-1 truncate text-dim">
                  {suggestion.memberKeys.join(" · ")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="compact"
                  className="text-brand-text"
                  onClick={() => onMerge(suggestion.memberKeys)}
                >
                  <GitMerge />
                  {t("import.merge")}
                </Button>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {loading ? (
        <FeedbackState state="loading" title={tc("loading")} />
      ) : keys.length === 0 ? (
        <FeedbackState state={searchActive ? "no-results" : "empty"} title={emptyText} />
      ) : (
        <div className="flex max-h-[calc(100vh-340px)] min-h-[220px] flex-col overflow-hidden rounded-card border border-hairline">
          <Table scrollLabel={t(`tabs.${kind}`)}>
            <TableHeader>
              <TableRow>
                <TableHead className={SELECT_CELL}>
                  <span className="sr-only">{t("import.colSelect")}</span>
                </TableHead>
                <TableHead>{t("import.colItem")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {all
                ? data.mergedRows.map((row, index) => {
                    const activeIdx = activeIndexFor(row.key);
                    const detail = row.sources[activeIdx]?.detail;
                    const isExpanded = expandedKeys.has(row.key);
                    const checkId = `${uid}-check-${index}`;
                    const variantsId = `${uid}-variants-${index}`;
                    const isMerge = !!row.mergeType && !!row.mergedKeys;
                    return [
                      <TableRow key={row.key} selected={selected.has(row.key)}>
                        <TableCell className={SELECT_CELL}>
                          <Checkbox
                            id={checkId}
                            checked={selected.has(row.key)}
                            onCheckedChange={() => toggle(row.key)}
                            aria-label={row.canonicalName || row.key}
                          />
                        </TableCell>
                        <TableCell variant="identity" wrap>
                          <div className="flex flex-wrap items-center gap-2">
                            {isMerge ? (
                              <div className="min-w-0 flex-1">
                                <Input
                                  size="compact"
                                  aria-label={t("import.mergedName")}
                                  value={row.canonicalName ?? ""}
                                  onChange={(e) => onRename(row.key, e.target.value)}
                                  className="max-w-[240px] font-medium"
                                />
                                <div className="mt-1 flex items-center gap-2 text-xs text-dim">
                                  <span className="min-w-0 truncate">
                                    {t("import.mergedFrom", { names: row.mergedKeys!.join(", ") })}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="link"
                                    size="compact"
                                    className="shrink-0"
                                    onClick={() => onUnmerge(row.key, row.mergedKeys!)}
                                  >
                                    {t("import.unmerge")}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <label
                                htmlFor={checkId}
                                className="min-w-0 cursor-pointer truncate font-medium"
                                title={row.key}
                              >
                                {row.key}
                              </label>
                            )}
                            {row.hasConflict && (
                              <Badge variant="warning">{t("import.conflict")}</Badge>
                            )}
                            <Badge variant="secondary">
                              {row.sources.length === appCount
                                ? t("import.inAllApps", { count: appCount })
                                : t("import.inSomeApps", {
                                    count: row.sources.length,
                                    total: appCount,
                                  })}
                            </Badge>
                          </div>
                          {detail && <ItemDetail>{detail}</ItemDetail>}
                          {row.hasConflict && (
                            <Button
                              type="button"
                              variant="link"
                              size="compact"
                              aria-expanded={isExpanded}
                              aria-controls={isExpanded ? variantsId : undefined}
                              onClick={() => onToggleExpanded(row.key)}
                            >
                              <ChevronDown className={cn(isExpanded && "rotate-180")} />
                              {t("import.viewVariants")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>,
                      row.hasConflict && isExpanded && (
                        <TableRow key={`${row.key}-variants`}>
                          <TableCell colSpan={2} id={variantsId} variant="identity" wrap>
                            <div
                              role="radiogroup"
                              aria-label={t("import.variantsOf", {
                                name: row.canonicalName || row.key,
                              })}
                              className="flex flex-col gap-1 rounded-card bg-muted p-2"
                            >
                              {row.sources.map((source, sourceIndex) => (
                                <label
                                  key={`${source.appId}-${sourceIndex}`}
                                  className={cn(
                                    "flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-surface",
                                    activeIdx === sourceIndex && "bg-surface",
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name={`${uid}-source-${index}`}
                                    checked={activeIdx === sourceIndex}
                                    onChange={() => onPickSource(row.key, sourceIndex)}
                                    className="mt-0.5 accent-primary"
                                  />
                                  <span className="min-w-0">
                                    <span className="block font-medium">{source.appName}</span>
                                    {source.detail && (
                                      <code className="block truncate font-mono text-dim">
                                        {source.detail}
                                      </code>
                                    )}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ),
                    ];
                  })
                : data.singleRows.map((row, index) => {
                    const checkId = `${uid}-check-${index}`;
                    return (
                      <TableRow key={row.id} selected={selected.has(row.key)}>
                        <TableCell className={SELECT_CELL}>
                          <Checkbox
                            id={checkId}
                            checked={selected.has(row.key)}
                            onCheckedChange={() => toggle(row.key)}
                          />
                        </TableCell>
                        <TableCell variant="identity">
                          <label htmlFor={checkId} className="block cursor-pointer">
                            <span className="block truncate font-medium" title={row.key}>
                              {row.key}
                            </span>
                            {row.detail && <ItemDetail>{row.detail}</ItemDetail>}
                          </label>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/** The one-line definition, field list or expression under an item's name. */
function ItemDetail({ children }: { children: string }) {
  return (
    <code className="mt-1 block max-w-full truncate rounded-sm bg-muted px-1.5 py-0.5 font-mono text-xs">
      {children}
    </code>
  );
}
